import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Copy, Download, ExternalLink, Eye, Mail, Percent, Timer, Pencil, LayoutTemplate } from "lucide-react";
import { toast } from "sonner";
import { api, fmtDuration, formatErr } from "@/lib/api";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { StatCard } from "@/components/StatCard";
import { StatusBadge, publicUrl } from "@/components/CampaignRow";
import { LeadsTable } from "@/components/LeadsTable";
import { RetentionChart } from "@/components/RetentionChart";
import { CountryBreakdown } from "@/components/CountryBreakdown";
import { ShortLinksPanel } from "@/components/ShortLinksPanel";
import { ManualEntriesPanel } from "@/components/ManualEntriesPanel";
import { useAuth } from "@/lib/auth";

const csvCell = (v) => {
  const s = Array.isArray(v) ? v.join("; ") : String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const SaveTemplateButton = ({ campaign: c }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(`${c.name} template`);
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/campaigns/${c.id}/save-as-template`, { name });
      toast.success(c.template_id ? "Template updated" : "Saved as template");
      setOpen(false);
    } catch (ex) { toast.error(formatErr(ex)); }
    finally { setBusy(false); }
  };
  if (!open)
    return (
      <button data-testid="detail-save-template-button" onClick={() => setOpen(true)} className="h-10 px-4 rounded-full border border-[#242C3D] hover:bg-white/5 text-sm flex items-center gap-2">
        <LayoutTemplate className="w-4 h-4" /> {c.template_id ? "Update template" : "Save as template"}
      </button>
    );
  return (
    <form onSubmit={save} className="flex gap-2" data-testid="save-template-form">
      <input data-testid="save-template-name-input" autoFocus required value={name} onChange={(e) => setName(e.target.value)} disabled={!!c.template_id}
        className="h-10 px-3 w-56 rounded-full bg-[#0B0F17] border border-[#242C3D] text-sm focus:border-indigo-500 focus:outline-none disabled:opacity-60" />
      <button data-testid="save-template-submit-button" disabled={busy} className="h-10 px-4 rounded-full bg-indigo-500 hover:bg-indigo-400 text-sm font-medium disabled:opacity-60">{busy ? "Saving…" : "Save"}</button>
      <button type="button" onClick={() => setOpen(false)} className="h-10 px-3 rounded-full border border-[#242C3D] text-sm hover:bg-white/5">✕</button>
    </form>
  );
};

function exportCsv(name, leads, formDef, hubQs = []) {
  const fields = formDef?.fields || [];
  const optins = formDef?.optins || [];
  const head = ["email", "captured_at", "country", ...fields.map((f) => `q:${f.label}`), ...optins.map((o) => `optin:${o.label}`), ...hubQs.map((q) => `hub:${q}`),
    "watched_seconds", "watched_hms", "furthest_position_seconds", "video_duration_seconds", "completion_percent", "sessions", "completed", "cta_clicked", "last_active"];
  const lines = leads.map((l) => [
    l.email, l.created_at, l.country, ...fields.map((f) => l.answers?.[f.id]), ...optins.map((o) => (l.optins?.[o.id] ? "yes" : "no")), ...hubQs.map((q) => l.hub_answers?.[q] || ""),
    l.watched_seconds, fmtDuration(l.watched_seconds), l.max_position, l.video_duration, l.completion, l.sessions, l.completed ? "yes" : "no", l.cta_clicked ? "yes" : "no", l.last_active,
  ].map(csvCell).join(","));
  const blob = new Blob([[head.map(csvCell).join(","), ...lines].join("\n")], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${name}-leads.csv`;
  a.click();
}

const dateCls = "h-8 px-2 rounded-md bg-[#0B0F17] border border-[#242C3D] text-xs font-mono text-slate-300 focus:border-indigo-500 focus:outline-none [color-scheme:dark]";

const DateRange = ({ value, onChange }) => {
  const [f, setF] = useState(value.from);
  const [t, setT] = useState(value.to);
  const custom = value.from || value.to;
  return (
    <div className="flex items-center gap-2" data-testid="traffic-date-range">
      <input type="date" data-testid="traffic-date-from" className={dateCls} value={f} max={t || undefined} onChange={(e) => setF(e.target.value)} />
      <span className="text-slate-500 text-xs">to</span>
      <input type="date" data-testid="traffic-date-to" className={dateCls} value={t} min={f || undefined} onChange={(e) => setT(e.target.value)} />
      <button type="button" data-testid="traffic-date-apply" disabled={!f && !t} onClick={() => onChange({ from: f, to: t })}
        className="h-8 px-3 rounded-full bg-indigo-500 hover:bg-indigo-400 text-xs font-medium disabled:opacity-40 transition-colors">Apply</button>
      {custom && <button type="button" data-testid="traffic-date-reset" onClick={() => { setF(""); setT(""); onChange({ from: "", to: "" }); }}
        className="h-8 px-3 rounded-full border border-[#242C3D] text-xs text-slate-300 hover:bg-white/5 transition-colors">Reset</button>}
    </div>
  );
};

export default function CampaignDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [range, setRange] = useState({ from: "", to: "" });
  const load = useCallback(() => api.get(`/campaigns/${id}/analytics`, {
    params: range.from || range.to ? { date_from: range.from || undefined, date_to: range.to || undefined } : {},
  }).then((r) => setD(r.data)).catch((e) => toast.error(formatErr(e))), [id, range]);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [load]);

  if (!d) return <div className="font-mono text-sm text-slate-500">Loading…</div>;
  const { campaign: c, stats: s } = d;

  return (
    <div>
      <Link to="/admin" data-testid="detail-back-link" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-6"><ArrowLeft className="w-4 h-4" /> All campaigns</Link>
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10 sv-rise">
        <div>
          <div className="flex items-center gap-3 mb-3"><StatusBadge status={c.status} /><span className="font-mono text-xs text-slate-500">/c/{c.slug}</span></div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight" data-testid="detail-campaign-name">{c.name}</h1>
          <p className="text-slate-400 mt-2">{c.title}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="detail-copy-link-button" onClick={() => { navigator.clipboard.writeText(publicUrl(c.slug)); toast.success("Link copied"); }} className="h-10 px-4 rounded-full border border-[#242C3D] hover:bg-white/5 text-sm flex items-center gap-2"><Copy className="w-4 h-4" /> Copy link</button>
          <SaveTemplateButton campaign={c} />
          <a data-testid="detail-open-landing-link" href={`/c/${c.slug}`} target="_blank" rel="noreferrer" className="h-10 px-4 rounded-full border border-[#242C3D] hover:bg-white/5 text-sm flex items-center gap-2"><ExternalLink className="w-4 h-4" /> Open page</a>
          <Link data-testid="detail-edit-button" to={`/admin/campaigns/${c.id}/edit`} className="h-10 px-4 rounded-full border border-[#242C3D] hover:bg-white/5 text-sm flex items-center gap-2"><Pencil className="w-4 h-4" /> Edit</Link>
          <button data-testid="export-csv-button" onClick={() => exportCsv(c.slug, d.leads.filter((l) => !l.is_test), c.form, d.hub_questions)} className="h-10 px-5 rounded-full bg-indigo-500 hover:bg-indigo-400 text-sm font-medium flex items-center gap-2"><Download className="w-4 h-4" /> Export CSV</button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard testid="detail-metric-visits" label="Visits" icon={Eye} value={s.visits} sub={`${s.unique_visitors} unique`} />
        <StatCard testid="detail-metric-leads" label="Leads" icon={Mail} accent="text-cyan-400" value={s.leads} sub={`${s.viewers} watched the video`} delay="sv-d1" />
        <StatCard testid="detail-metric-conversion" label="Conversion" icon={Percent} accent="text-emerald-400" value={s.conversion_rate == null ? "—" : `${s.conversion_rate}%`} sub={`${s.completions} finished · ${s.cta_clicks} CTA clicks`} delay="sv-d2" />
        <StatCard testid="detail-metric-avg-watch" label="Avg watch / viewer" icon={Timer} accent="text-amber-400" value={fmtDuration(s.avg_watch_seconds)} sub={`${fmtDuration(s.total_watch_seconds)} total`} delay="sv-d3" />
      </div>

      <div className="grid lg:grid-cols-[2fr_1fr] gap-4 mb-8">
        <div className="rounded-lg border border-[#242C3D] bg-[#141A24]/70 p-6" data-testid="detail-traffic-chart">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="font-display font-semibold" data-testid="traffic-chart-title">Traffic · {range.from || range.to ? "custom range" : "14 days of activity"}
              {d.daily?.length > 0 && <span className="ml-2 font-mono text-xs font-normal text-slate-500">{d.daily[0].date} → {d.daily[d.daily.length - 1].date}</span>}</h3>
            <DateRange value={range} onChange={setRange} />
          </div>
          <div className="h-56 min-h-[224px] w-full">
            <ResponsiveContainer width="100%" height={224}>
              <AreaChart data={d.daily}>
                <defs>
                  <linearGradient id="gv" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#6366F1" stopOpacity={0.4} /><stop offset="100%" stopColor="#6366F1" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid stroke="#242C3D" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(v) => v.slice(5)} stroke="#64748b" fontSize={11} />
                <YAxis allowDecimals={false} stroke="#64748b" fontSize={11} width={28} />
                <Tooltip contentStyle={{ background: "#141A24", border: "1px solid #242C3D", borderRadius: 8 }} />
                <Area type="monotone" dataKey="visits" stroke="#6366F1" fill="url(#gv)" strokeWidth={2} />
                <Area type="monotone" dataKey="leads" stroke="#06B6D4" fill="transparent" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-lg border border-[#242C3D] bg-[#141A24]/70 p-6" data-testid="detail-traffic-sources">
          <h3 className="font-display font-semibold mb-4">Traffic sources</h3>
          {d.sources.length === 0 ? <p className="text-sm text-slate-500">No visits yet. Add <span className="font-mono">?utm_source=…</span> to your links to track sources.</p> : (
            <ul className="space-y-3">
              {d.sources.map((x) => (
                <li key={x.source}>
                  <div className="flex justify-between text-sm mb-1"><span className="text-slate-300">{x.source}</span><span className="font-mono text-slate-400">{x.count}</span></div>
                  <div className="h-1.5 rounded bg-[#0B0F17]"><div className="h-full rounded bg-indigo-500" style={{ width: `${(x.count / s.visits) * 100}%` }} /></div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_2fr] gap-4 mb-8 items-start">
        <CountryBreakdown data={d.countries} testid="detail-country-breakdown" />
        <ShortLinksPanel campaign={c} />
      </div>

      <RetentionChart data={d.retention} />

      {user?.role === "admin" && <ManualEntriesPanel campaign={c} onDone={load} />}

      <LeadsTable leads={d.leads} formDef={c.form} campaignId={c.id} hubQuestions={d.hub_questions} onChanged={load} />
    </div>
  );
}
